import { Signer } from '@ethersproject/abstract-signer'
import { Provider } from '@ethersproject/providers'
import { assertEx, EthAddress, forget } from '@xylabs/sdk-js'
import { TypedEvent } from '@xyo-network/world-typechain'

import { XyoWorldBase } from '../../Base'
import { XyoWorldGeotokensWrapper } from '../../EthersClients'
import { BaseJsonObject } from '../../models'
import { Quadkey } from '../../Quadkey'
import { XyoNetworkWrapper } from '../../wrappers'
import { MinterDeployBlocks } from './DeployBlocks'
import { Events, GeotokensEventsJsonObject, quadkeysFromGeotokensEvent } from './Events'
import { XyoWorldGeotoken } from './Geotoken'
import { GeotokenFactoryJsonObject, XyoWorldGeotokenFactory } from './GeotokenFactory'

export interface XyoWorldGeotokensJsonObject extends BaseJsonObject {
  blockNumber?: number
  events?: GeotokensEventsJsonObject
  geotokens?: GeotokenFactoryJsonObject
}

export class XyoWorldGeotokens extends XyoWorldBase<XyoWorldGeotokensJsonObject> {
  static geotokens?: XyoWorldGeotokens

  public blockNumber?: number

  public provider: Provider

  public signer?: Signer

  public wrapper: XyoWorldGeotokensWrapper

  private _address?: EthAddress

  private _events?: Events

  private _factory?: XyoWorldGeotokenFactory

  private constructor(network: XyoNetworkWrapper, provider: Provider, signer?: Signer, json?: XyoWorldGeotokensJsonObject) {
    super()
    const currentGeotokensAddress = assertEx(network.addresses.geotokens, 'Minter Address Required')
    this.provider = provider
    this.signer = signer
    this.wrapper = new XyoWorldGeotokensWrapper(currentGeotokensAddress, provider, signer)
    this.loadJsonObject(json)
    this.events().monitorEvents((event) => {
      forget(this.updateWithEvents([event]))
    })
  }

  static get(network: XyoNetworkWrapper, provider: Provider, signer?: Signer, jsonObject?: XyoWorldGeotokensJsonObject) {
    this.geotokens = new XyoWorldGeotokens(network, provider, signer, jsonObject)
    return this.geotokens
  }

  public address() {
    this._address = this._address || assertEx(EthAddress.fromString(this.wrapper.contract.address))
    return this._address
  }

  public clear() {
    this.factory().clear()
    this.blockNumber = undefined
    return this
  }

  public events(): Events {
    this._events = this._events || new Events(this.wrapper.contract)
    return this._events
  }

  public factory() {
    this._factory = this._factory || new XyoWorldGeotokenFactory(this.wrapper)
    return this._factory
  }

  public listGeotokens() {
    return this.factory().getAll()
  }

  public loadJsonObject(jsonObject?: XyoWorldGeotokensJsonObject) {
    if (jsonObject) {
      this.blockNumber = jsonObject.blockNumber
      this.factory().loadJsonObject(jsonObject.geotokens)
    }
    return this
  }

  public async refresh() {
    this.clear()
    await this.updateWithAllEvents()
    return this
  }

  public toJsonObject(): XyoWorldGeotokensJsonObject {
    return {
      blockNumber: this.blockNumber,
      geotokens: this.factory().toJsonObject(),
    }
  }

  public toJsonObjectWithEvents(): XyoWorldGeotokensJsonObject {
    return {
      events: this.events().toJsonObject(),
      ...this.toJsonObject(),
    }
  }

  public async updateWithAllEvents() {
    this.blockNumber = undefined
    const currentBlockNumber = await this.provider.getBlockNumber()
    const events = await this.events().all()
    await this.updateWithEvents(events)
    this.blockNumber = currentBlockNumber
    return this
  }

  public async updateWithEvents(events: TypedEvent[]) {
    this.blockNumber = this.blockNumber ?? MinterDeployBlocks.v3
    const eventsByQuadkey = new Map<string, TypedEvent[]>()
    for (const event of events) {
      const quadkeys = quadkeysFromGeotokensEvent(event)
      for (const quadkey of quadkeys) {
        let eventList = eventsByQuadkey.get(quadkey.toBase4HashLabel())
        if (!eventList) {
          eventList = []
          eventsByQuadkey.set(quadkey.toBase4HashLabel(), eventList)
        }
        eventList.push(event)
      }
      // update the blockNumber so if updateWithNewEvents is called, it knows where to start
      if (this.blockNumber && event.blockNumber > this.blockNumber) {
        this.blockNumber = event.blockNumber
      }
    }

    const promises: Promise<XyoWorldGeotoken>[] = []

    for (const pair of eventsByQuadkey) {
      const quadkey = Quadkey.fromBase4String(pair[0])
      if (quadkey) {
        const events = pair[1]
        if (events.length > 0) {
          promises.push(this.factory().get(quadkey).updateWithEvents(events))
        }
      }
    }
    await Promise.all(promises)
    return this
  }

  public async updateWithNewEvents(maxBlocks = 10000) {
    const currentBlockNumber = await this.provider.getBlockNumber()
    const blockTo = currentBlockNumber < (this.blockNumber ?? 0) + maxBlocks ? currentBlockNumber : (this.blockNumber ?? 0) + maxBlocks
    const events = await this.events().all(this.blockNumber, blockTo)
    await this.updateWithEvents(events)
    this.blockNumber = blockTo
    return this
  }
}
